import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { revenueReportApproved } from "store/actions/revenueReportActions";
import { refreshTodoNavigationItems } from "store/actions/todoNavigationActions";
//#endregion
//#region types
import { AxiosResponse } from "axios";
import { TodoCardProps } from "models/propTypes/todoPropTypes";
import { CurrencyEnums, FileEnums, StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { Amount } from "models/utilTypes/currencyType";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { WintSuperkollAPIModel as ApiModels, WintUtilsWebApi } from "wint-api-client/dist/types";
//#endregion
//#region components
import {
  EntityCardContent,
  FileUploaderContainer,
  Icon,
  PageFooter,
  StaticInfoMessage,
  StyledButton,
  TableAsync,
  Tags,
  TitledContainerInner,
} from "components/common";
import TopTodoCard from "components/todos/_partials/topTodoCard";
import BaseInput from "components/common/wintForms/formFields/baseInput";
import ToggleSwitch from "components/common/forms/baseFields/toggleSwitch";
import ExpandableSection from "components/common/wintForms/formSections/expandableSection";
import {
  formatRevenueReportingPeriod,
  getRevenueReportingPeriod,
  isDayWindow,
  useCreateRevenueReport,
  useRevenueReportSnoozeAction,
} from "components/revenueReporting";
import { useCreateCashReconciliationDialog } from "components/cashReconciliation";
import { useRevenueReportingService } from "serviceHooks";
import { Grid } from "@mui/material";
//#endregion
import { LocalizationHelperInstance, TableHelper, UrlHelper } from "utils/helpers";
import { IconDefinitionConstants } from "utils/constants";
import { MaxFileSize } from "utils/constants/fileConstants";
import cssExports from "theme/styles/main.module.scss";
import { camelize } from "humps";

const AllowedAttachmentTypes = [
  FileEnums.ContentTypes.ImageJPEG,
  FileEnums.ContentTypes.ImageJPG,
  FileEnums.ContentTypes.ImagePNG,
  FileEnums.ContentTypes.ApplicationPDF,
];

const ReportsPerPage = 10;
const MaxReportsFetch = 500;

const toApprovalAttachment = (file: UploadedFile): ApiModels.RevenueReportAttachment => ({
  contentType: file.contentType,
  data: file.data,
  fileName: file.fileName,
});

const RevenueReportApprovalTodo: React.FC<TodoCardProps> = (props) => {
  const detail = props.item.revenueReportNotSubmitted;
  const todoId = props.item.guidId ?? props.item.id;
  const revenueReportingService = useRevenueReportingService();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const snoozeAction = useRevenueReportSnoozeAction(todoId, () => props.onActionSuccess?.());

  const [loading, setLoading] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [reports, setReports] = React.useState<ApiModels.RevenueReportDescriptor[]>([]);
  const [comment, setComment] = React.useState("");
  const [confirmNoIncome, setConfirmNoIncome] = React.useState(false);
  const [attachments, setAttachments] = React.useState<UploadedFile[]>([]);

  const period = React.useMemo(() => getRevenueReportingPeriod(detail), [detail]);

  const periodLabel = React.useMemo(() => formatRevenueReportingPeriod(detail), [detail]);

  const fetchData = React.useCallback(async () => {
    if (!detail) {
      return;
    }
    setLoading(true);
    try {
      const response = await revenueReportingService.getReports(
        {
          templateId: detail.templateId,
          status: ApiModels.RevenueReportStatus.Reported,
          fromDate: period.firstDay,
          toDate: period.lastDay,
          page: 0,
          numPerPage: MaxReportsFetch,
          orderByDescending: true,
          thenByDescending: false,
          isAutoPaginating: false,
        },
        { showErrors: true }
      );
      setReports(response.data?.items ?? []);
      setLoaded(true);
    } catch {
      setReports([]);
      setLoaded(false);
    } finally {
      setLoading(false);
    }
  }, [detail, period.firstDay, period.lastDay]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const cashReconciliationDialog = useCreateCashReconciliationDialog(fetchData);
  const onAddReport = useCreateRevenueReport(cashReconciliationDialog, fetchData, detail?.templateId, period.lastDay);

  const total = React.useMemo(() => reports.reduce((sum, report) => sum + report.amount, 0), [reports]);

  const reportsFilter = React.useCallback(
    (baseFilter?: WintUtilsWebApi.Filter) => {
      const perPage = baseFilter?.numPerPage || ReportsPerPage;
      const currentPage = baseFilter?.page ?? 0;
      const start = currentPage * perPage;
      // TableAsync delegates ordering to this callback for function filters and sends the
      // column pascalized, so the whole result set is sorted before it is paged.
      const orderBy = camelize(baseFilter?.orderByProperty ?? "date");
      const sorted = TableHelper.tableSortAsync(
        [...reports],
        TableHelper.getComparator(baseFilter?.orderByDescending ? "desc" : "asc", orderBy)
      );
      return Promise.resolve({
        data: {
          items: sorted.slice(start, start + perPage),
          page: currentPage,
          numPerPage: perPage,
          totalItems: reports.length,
          totalItemsWithOutFilter: reports.length,
          summaryItem: { amount: total } as ApiModels.RevenueReportDescriptor,
        },
      } as AxiosResponse<WintUtilsWebApi.ListResponse<ApiModels.RevenueReportDescriptor>>);
    },
    [reports, total]
  );

  const hasReports = reports.length > 0;
  const noIncomeRequired = loaded && !hasReports;
  const approveDisabled = (noIncomeRequired && !confirmNoIncome) || (!loaded && !hasReports);

  const onApprove = () => {
    const request: ApiModels.RevenueReportApproveRequest = {
      templateId: detail?.templateId,
      periodYear: detail?.periodYear ?? 0,
      periodMonth: detail?.periodMonth ?? 0,
      periodStartDay: isDayWindow(detail) ? detail?.periodStartDay : undefined,
      confirmNoIncome: noIncomeRequired ? confirmNoIncome : false,
      comment: comment || undefined,
      attachments: attachments.length ? attachments.map(toApprovalAttachment) : undefined,
    };

    return revenueReportingService.approve(request);
  };

  const onFilesUpdated = (files?: UploadedFile[]) => {
    setAttachments(files ?? []);
  };

  const onCancelUpload = (index: number) => {
    setAttachments((previous) => previous.filter((_, fileIndex) => fileIndex !== index));
  };

  const onRowClick = (row: ApiModels.RevenueReportDescriptor) => {
    if (!row.id) {
      return;
    }
    navigate(UrlHelper.formatPath(`/RevenueReport/${row.id}`), {
      state: { background: location, preserveSearch: true },
    });
  };

  return (
    <EntityCardContent
      loading={loading}
      topTodoCard={
        <TopTodoCard
          item={props.item}
          descriptionHeadingOne={detail?.templateName}
          descriptionHeadingTwo={periodLabel}
          footerText={
            props.item.dueDate
              ? `${LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.Common.Due_Date
                )}: ${LocalizationHelperInstance.getFormattedDate(props.item.dueDate)}`
              : ""
          }
          quickActions={[snoozeAction]}
          noDescriptionTextWrap
        />
      }
      betweenCardsBanner={
        detail?.previousPeriodUnapproved ? (
          <StaticInfoMessage displayType={StyleEnums.DisplayType.Warning}>
            {LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.RevenueReporting.PreviousPeriodUnapprovedWarning
            )}
          </StaticInfoMessage>
        ) : undefined
      }
      todoMainSection={
        <TitledContainerInner
          header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.RevenueReporting.ApprovalTodoHeader)}
          subHeader={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.RevenueReporting.ApprovalTodoSubHeader
          )}
        >
          <Grid container item direction="column" sx={{ rowGap: 3, mt: 2 }}>
            <Grid item>
              <StyledButton
                displayType={StyleEnums.DisplayType.Info}
                onClick={onAddReport}
                endIcon={<Icon icon={IconDefinitionConstants.WPlus} />}
                testId="addRevenueReport"
              >
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.RevenueReporting.AddReport)}
              </StyledButton>
            </Grid>

            {noIncomeRequired && (
              <Grid container item alignItems="center" justifyContent="space-between">
                <span className={cssExports.label}>
                  {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.RevenueReporting.ReportNoIncome)}
                </span>
                <ToggleSwitch checked={confirmNoIncome} onChange={(_, checked) => setConfirmNoIncome(checked)} />
              </Grid>
            )}

            <Grid container item direction="column" spacing={2}>
              <Grid container item direction="column" spacing={0.5}>
                <Grid item>
                  <span className={cssExports.formControlLabel}>
                    {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.RevenueReporting.CommentLabel)}
                  </span>
                </Grid>
                <Grid item>
                  <span className={cssExports.formControlDescription}>
                    {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.RevenueReporting.CommentHelpText)}
                  </span>
                </Grid>
              </Grid>
              <Grid item>
                <BaseInput
                  type="text"
                  icon={IconDefinitionConstants.WCommentDots}
                  className={cssExports.revenueReportCommentInput}
                  multiline
                  minRows={1}
                  maxRows={5}
                  name="revenueReportComment"
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  onBlur={() => undefined}
                  setValue={(value: string) => setComment(value)}
                  setTouched={() => undefined}
                  touched={false}
                  hasError={false}
                  placeholder={LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.RevenueReporting.CommentPlaceholder
                  )}
                  optional
                />
              </Grid>
            </Grid>

            <Grid item>
              <ExpandableSection
                summary={
                  <span>
                    {LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.RevenueReporting.SupplementaryMaterial
                    )}
                  </span>
                }
              >
                <Grid container item direction="column" spacing={2}>
                  <Grid item>
                    <span className={cssExports.formControlDescription}>
                      {LocalizationHelperInstance.getFormattedMessage(
                        TranslationKeys.RevenueReporting.SupplementaryMaterialHelpText
                      )}
                    </span>
                  </Grid>
                  <Grid item>
                    <FileUploaderContainer
                      fileUploaderType="full"
                      filesToUpload={attachments}
                      onFilesUpdated={onFilesUpdated}
                      onCancelUpload={onCancelUpload}
                      allowAnyContentType={false}
                      allowedTypes={AllowedAttachmentTypes}
                      maxFileSize={MaxFileSize}
                    />
                  </Grid>
                </Grid>
              </ExpandableSection>
            </Grid>
          </Grid>
        </TitledContainerInner>
      }
      infoViewer={
        hasReports ? (
          <TableAsync<ApiModels.RevenueReportDescriptor>
            properties={{
              date: {
                description: TranslationKeys.Common.Date,
                formatter: (report: ApiModels.RevenueReportDescriptor) => new Date(report.date),
                noWrap: true,
                maxWidth: 130,
              },
              ownerName: {
                description: TranslationKeys.CashReconciliation.RegisteredBy,
                formatter: (report: ApiModels.RevenueReportDescriptor) => report.ownerName ?? "",
                noWrap: true,
                maxWidth: 220,
              },
              costCenter: {
                description: TranslationKeys.Settings.CostCenter,
                formatter: (report: ApiModels.RevenueReportDescriptor) => (
                  <Tags tags={report.costCenter ? [report.costCenter] : []} maxDisplayCount={1} />
                ),
                maxWidth: 120,
                noWrap: true,
                disableSort: true,
              },
              project: {
                description: TranslationKeys.Settings.Project,
                formatter: (report: ApiModels.RevenueReportDescriptor) => (
                  <Tags tags={report.project ? [report.project] : []} maxDisplayCount={1} />
                ),
                maxWidth: 120,
                noWrap: true,
                disableSort: true,
              },
              difference: {
                description: TranslationKeys.RevenueReporting.Difference,
                formatter: (report: ApiModels.RevenueReportDescriptor) =>
                  new Amount(CurrencyEnums.CurrencyCode.SEK, report.difference),
                width: 200,
                alignment: "right",
                noWrap: true,
                classification: "amount",
              },
              amount: {
                description: TranslationKeys.Common.Amount,
                formatter: (report: ApiModels.RevenueReportDescriptor) =>
                  new Amount(CurrencyEnums.CurrencyCode.SEK, report.amount),
                summaryValueFormatter: (values: ApiModels.RevenueReportDescriptor) =>
                  new Amount(CurrencyEnums.CurrencyCode.SEK, values.amount),
                width: 200,
                alignment: "right",
                noWrap: true,
                className: cssExports.textBlack,
              },
            }}
            filter={reportsFilter}
            rowClickHandler={onRowClick}
            tableId="revenueReportApprovalTodoReports"
            orderByProperty="date"
            orderByDescending
            disableQueryPagination
            includeSummary
            fetchSummaryWithData
            customSummaryInfo={
              <span className={cssExports.label}>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.InTotal)}:
              </span>
            }
            maxRowCount={ReportsPerPage}
            hidePaginationIfSinglePage
            reloadKey={reports.length}
          />
        ) : undefined
      }
      footer={
        <PageFooter
          actions={[
            {
              text: hasReports ? TranslationKeys.Common.Approve : TranslationKeys.RevenueReporting.Submit,
              onClick: onApprove,
              type: StyleEnums.DisplayType.Success,
              successMessage: TranslationKeys.RevenueReporting.ApproveTodoSuccessMessage,
              isDisabled: approveDisabled,
              onActionSuccess: () => {
                props.onActionSuccess?.();
                dispatch(revenueReportApproved(todoId));
                dispatch(refreshTodoNavigationItems());
              },
            },
          ]}
        />
      }
    ></EntityCardContent>
  );
};

export default RevenueReportApprovalTodo;
